import React, { useState } from 'react';
import { PageSection, SiteData, SitePage } from '../../../types';
import { Mail, Phone, MapPin, Send, CheckCircle2, Loader2, AlertCircle } from 'lucide-react';
import { communicationService } from '../../../services/communicationService';
import { checkSubmissionRateLimit } from '../../../utils/security';
import { isDarkBackground, getOptimalTextColor, getOptimalMutedTextColor } from '../../../utils/contrast';

interface SectionProps {
  section: PageSection;
  siteData: SiteData;
  activePage?: SitePage;
}

export const ContactSection: React.FC<SectionProps> = ({ section, siteData, activePage }) => {
  const content = section.content || {};
  const style = section.style || {};

  const badge = content.badge || 'İLETİŞİME GEÇİN';
  const title = content.title || 'Sorularınız ve Teklif Talepleriniz İçin Buradayız';
  const description =
    content.description ||
    'Formu doldurarak veya doğrudan iletişim kanallarımızdan bize ulaşabilirsiniz.';
  const buttonText = content.buttonText || 'Mesaj Gönder';

  const [formSent, setFormSent] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', message: '' });
  const [honeypot, setHoneypot] = useState('');

  const targetSiteId = siteData?.id || (siteData as any)?.siteId || 'default-site';
  const bgColor = style.backgroundColor || (style as any)?.bgColor || '#0F172A';
  const isDark = isDarkBackground(bgColor);
  const textColor = style.textColor || getOptimalTextColor(bgColor);
  const mutedTextColor = getOptimalMutedTextColor(bgColor);
  const accentColor = siteData.settings?.accentColor || '#2563EB';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (honeypot) return; // Silent discard for spam bots

    // Validation
    const cleanName = formData.name.trim();
    const cleanEmail = formData.email.trim();
    const cleanMessage = formData.message.trim();

    if (!cleanName || !cleanEmail || !cleanMessage) {
      setErrorMsg('Lütfen adınızı, e-posta adresinizi ve mesajınızı doldurun.');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(cleanEmail)) {
      setErrorMsg('Lütfen geçerli bir e-posta adresi yazınız.');
      return;
    }

    // Rate limit check: max 5 attempts per 60s
    const isAllowed = checkSubmissionRateLimit(`contact_${targetSiteId}`, 5, 60000);
    if (!isAllowed) {
      setErrorMsg('Çok fazla mesaj denemesi yaptınız. Lütfen 1 dakika sonra tekrar deneyin.');
      return;
    }

    setIsSubmitting(true);
    setErrorMsg(null);

    try {
      await communicationService.createCommunication(targetSiteId, {
        name: cleanName,
        email: cleanEmail,
        phone: formData.phone.trim() || undefined,
        subject: 'Web Sitesi İletişim Formu',
        message: cleanMessage,
        source: 'contact_form',
        sourcePageSlug: activePage?.slug || '/',
        sourcePageTitle: activePage?.title || 'İletişim',
        tenantId: siteData.ownerId,
        status: 'NEW',
      });
      setFormSent(true);
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (err: any) {
      console.error('Contact form submission error:', err);
      setErrorMsg(
        err.message ||
          'Mesaj gönderilirken bir sorun oluştu. Lütfen doğrudan telefon veya e-posta ile iletişime geçin.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section
      id={style.anchorId || style.customId || 'contact-section'}
      className="py-20 px-4 sm:px-6 lg:px-8"
      style={{ backgroundColor: bgColor, color: textColor }}
    >
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        {/* Contact Info Column */}
        <div className="space-y-6">
          {badge && (
            <div
              className={`inline-block px-3.5 py-1 rounded-full text-xs font-extrabold uppercase tracking-wider ${
                isDark ? 'bg-white/10' : 'bg-black/5'
              }`}
              style={{ color: accentColor }}
            >
              {badge}
            </div>
          )}
          <h2 className="text-3xl sm:text-5xl font-extrabold tracking-tight leading-tight">
            {title}
          </h2>
          <p
            className="text-sm sm:text-base leading-relaxed"
            style={{ color: mutedTextColor }}
          >
            {description}
          </p>

          <div className="space-y-4 pt-4 text-xs sm:text-sm">
            {siteData.settings?.phone && (
              <a
                href={`tel:${siteData.settings.phone.replace(/[^0-9+]/g, '')}`}
                className={`flex items-center gap-3 p-4 rounded-2xl border transition group ${
                  isDark
                    ? 'bg-white/5 border-white/10 hover:bg-white/10 text-slate-200'
                    : 'bg-slate-50 border-slate-200/80 hover:bg-slate-100 text-slate-800 shadow-xs'
                }`}
              >
                <Phone className="w-5 h-5 text-blue-500 shrink-0 group-hover:scale-110 transition-transform" />
                <span className="font-semibold">{siteData.settings.phone}</span>
              </a>
            )}
            {siteData.settings?.whatsapp && (
              <a
                href={`https://wa.me/${siteData.settings.whatsapp.replace(/[^0-9]/g, '')}`}
                target="_blank"
                rel="noreferrer"
                className={`flex items-center justify-between gap-3 p-4 rounded-2xl border transition group ${
                  isDark
                    ? 'bg-emerald-500/10 border-emerald-500/30 hover:bg-emerald-500/20 text-emerald-300'
                    : 'bg-emerald-50 border-emerald-200 hover:bg-emerald-100 text-emerald-800 shadow-xs'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 rounded-full bg-emerald-500 animate-ping" />
                  <span className="font-bold">WhatsApp İle Hızlı Mesajlaşın</span>
                </div>
                <span className="text-xs bg-emerald-600 px-2.5 py-1 rounded-full text-white font-semibold">
                  Hemen Başlat →
                </span>
              </a>
            )}
            {siteData.settings?.email && (
              <a
                href={`mailto:${siteData.settings.email}`}
                className={`flex items-center gap-3 p-4 rounded-2xl border transition ${
                  isDark
                    ? 'bg-white/5 border-white/10 hover:bg-white/10 text-slate-200'
                    : 'bg-slate-50 border-slate-200/80 hover:bg-slate-100 text-slate-800 shadow-xs'
                }`}
              >
                <Mail className="w-5 h-5 text-blue-500 shrink-0" />
                <span>{siteData.settings.email}</span>
              </a>
            )}
            {siteData.settings?.address && (
              <div
                className={`flex items-center gap-3 p-4 rounded-2xl border ${
                  isDark
                    ? 'bg-white/5 border-white/10 text-slate-200'
                    : 'bg-slate-50 border-slate-200/80 text-slate-800 shadow-xs'
                }`}
              >
                <MapPin className="w-5 h-5 text-blue-500 shrink-0" />
                <span>{siteData.settings.address}</span>
              </div>
            )}
            {siteData.settings?.workingHours && (
              <div
                className="text-xs px-2 flex items-center gap-2"
                style={{ color: mutedTextColor }}
              >
                <span>🕒 Çalışma Saatleri:</span>
                <span className="font-semibold">{siteData.settings.workingHours}</span>
              </div>
            )}
          </div>
        </div>

        {/* Contact Form Card */}
        <div
          className={`p-8 rounded-3xl border shadow-2xl backdrop-blur-md ${
            isDark
              ? 'bg-white/10 border-white/20 text-white'
              : 'bg-white border-slate-200/80 text-slate-900 shadow-xl'
          }`}
        >
          {formSent ? (
            <div className="text-center py-12 space-y-4">
              <div className="w-16 h-16 rounded-full bg-emerald-500/20 text-emerald-500 flex items-center justify-center mx-auto">
                <CheckCircle2 className="w-8 h-8" />
              </div>
              <h3 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                Mesajınız Alındı!
              </h3>
              <p
                className="text-xs max-w-xs mx-auto"
                style={{ color: mutedTextColor }}
              >
                Talebiniz ekibimize iletildi. En kısa sürede sizinle iletişime geçeceğiz.
              </p>
              <button
                type="button"
                onClick={() => setFormSent(false)}
                className={`px-4 py-2 rounded-xl text-xs font-bold transition ${
                  isDark
                    ? 'bg-white/20 text-white hover:bg-white/30'
                    : 'bg-slate-100 text-slate-800 hover:bg-slate-200 border border-slate-300'
                }`}
              >
                Yeni Mesaj Gönder
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Honeypot for bot filtering */}
              <div className="hidden" aria-hidden="true">
                <input
                  type="text"
                  name="company_website_url_honeypot"
                  tabIndex={-1}
                  value={honeypot}
                  onChange={(e) => setHoneypot(e.target.value)}
                  autoComplete="off"
                />
              </div>

              {errorMsg && (
                <div className="p-3 rounded-xl bg-rose-500/20 border border-rose-500/30 text-rose-600 dark:text-rose-200 text-xs flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 shrink-0 text-rose-500" />
                  <span>{errorMsg}</span>
                </div>
              )}

              <div className="space-y-1">
                <label
                  className={`text-xs font-bold ${
                    isDark ? 'text-slate-200' : 'text-slate-700'
                  }`}
                >
                  Adınız Soyadınız
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Ad Soyad"
                  className={`w-full px-4 py-3 rounded-xl text-xs sm:text-sm outline-none transition focus:ring-2 focus:ring-blue-500/40 ${
                    isDark
                      ? 'bg-slate-900/60 border border-white/20 text-white placeholder-slate-400 focus:border-blue-400'
                      : 'bg-slate-50 border border-slate-300 text-slate-900 placeholder-slate-400 focus:border-blue-500 focus:bg-white'
                  }`}
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label
                    className={`text-xs font-bold ${
                      isDark ? 'text-slate-200' : 'text-slate-700'
                    }`}
                  >
                    E-posta
                  </label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="ornek@sirket.com"
                    className={`w-full px-4 py-3 rounded-xl text-xs sm:text-sm outline-none transition focus:ring-2 focus:ring-blue-500/40 ${
                      isDark
                        ? 'bg-slate-900/60 border border-white/20 text-white placeholder-slate-400 focus:border-blue-400'
                        : 'bg-slate-50 border border-slate-300 text-slate-900 placeholder-slate-400 focus:border-blue-500 focus:bg-white'
                    }`}
                  />
                </div>
                <div className="space-y-1">
                  <label
                    className={`text-xs font-bold ${
                      isDark ? 'text-slate-200' : 'text-slate-700'
                    }`}
                  >
                    Telefon
                  </label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="+90 5XX XXX XX XX"
                    className={`w-full px-4 py-3 rounded-xl text-xs sm:text-sm outline-none transition focus:ring-2 focus:ring-blue-500/40 ${
                      isDark
                        ? 'bg-slate-900/60 border border-white/20 text-white placeholder-slate-400 focus:border-blue-400'
                        : 'bg-slate-50 border border-slate-300 text-slate-900 placeholder-slate-400 focus:border-blue-500 focus:bg-white'
                    }`}
                  />
                </div>
              </div>

              <div className="space-y-1">
                <label
                  className={`text-xs font-bold ${
                    isDark ? 'text-slate-200' : 'text-slate-700'
                  }`}
                >
                  Mesajınız
                </label>
                <textarea
                  required
                  rows={4}
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  placeholder="Hizmet talebiniz veya sorunuz..."
                  className={`w-full px-4 py-3 rounded-xl text-xs sm:text-sm outline-none transition resize-none focus:ring-2 focus:ring-blue-500/40 ${
                    isDark
                      ? 'bg-slate-900/60 border border-white/20 text-white placeholder-slate-400 focus:border-blue-400'
                      : 'bg-slate-50 border border-slate-300 text-slate-900 placeholder-slate-400 focus:border-blue-500 focus:bg-white'
                  }`}
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-3.5 rounded-xl font-bold text-xs sm:text-sm text-white shadow-xl hover:shadow-2xl transition flex items-center justify-center gap-2 disabled:opacity-50 active:scale-98"
                style={{ backgroundColor: accentColor }}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Gönderiliyor...</span>
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    <span>{buttonText}</span>
                  </>
                )}
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};

